"use client";

import { Fragment, useState, type ReactNode } from "react";
import Link from "next/link";

// Lucide-Pfade als Inline-SVG (das Paket hängt nicht an `lucide-react`, wie `seiten-icons-pfade.ts`): Check, Minus, Ban, Dot, ChevronDown/Right.
function Symbol({ className, children, punkt = false }: { className: string; children: ReactNode; punkt?: boolean }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill={punkt ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}
const Check = ({ className }: { className: string; "aria-hidden"?: string }) => (
  <Symbol className={className}>
    <path d="M20 6 9 17l-5-5" />
  </Symbol>
);
const Minus = ({ className }: { className: string; "aria-hidden"?: string }) => (
  <Symbol className={className}>
    <path d="M5 12h14" />
  </Symbol>
);
const Ban = ({ className }: { className: string; "aria-hidden"?: string }) => (
  <Symbol className={className}>
    <circle cx="12" cy="12" r="10" />
    <path d="m4.9 4.9 14.2 14.2" />
  </Symbol>
);
const Dot = ({ className }: { className: string; "aria-hidden"?: string }) => (
  <Symbol className={className} punkt>
    <circle cx="12.1" cy="12.1" r="1" />
  </Symbol>
);
const ChevronDown = ({ className }: { className: string; "aria-hidden"?: string }) => (
  <Symbol className={className}>
    <path d="m6 9 6 6 6-6" />
  </Symbol>
);
const ChevronRight = ({ className }: { className: string; "aria-hidden"?: string }) => (
  <Symbol className={className}>
    <path d="m9 18 6-6-6-6" />
  </Symbol>
);

/*
 * Matrix-Tabelle (Archetyp F, Design-Guide `matrix-tabelle.md` und `tabellen.md` „Matrix“, Musterkatalog M-130; Betreiber 20.10.2026
 * „Empfehlung“ 1A): Zeilen = Rechte (gruppiert in einklappbaren Bändern), Spalten = Rollen (in einem zweizeiligen Kopf nach Rollenart
 * gruppiert), stehender Kopf und stehende erste Spalte, eine Zelle = ein Zustand. Der Baustein ist NUR lesend: er filtert, sortiert
 * und lädt nichts; was der Aufrufer nicht übergibt, steht nicht da (r110). Zahlen und Zustände kommen fertig vom Aufrufer.
 *
 * Zellen tragen Symbol UND Text für Hilfstechnik („<Zeile>, <Spalte>: erlaubt“), nie nur Farbe (K8/R-F: Lucide, keine Zeichen).
 *
 * Abweichung AW-140: Rahmen `max-h-screen` statt `max-h-80..96` (die Matrix ist die Seite, nicht ein Block darin; der Kopf bleibt im
 * Rahmen stehen, wenn dieser scrollt). Abweichung AW-141: zweizeiliger Spaltengruppenkopf (`colSpan`) mit Kennzeichen für Drittsysteme
 * (nicht im Matrix-Eintrag des Design-Guides).
 */

export interface MatrixSpaltenGruppe {
  id: string;
  titel: string;
  /** Kennzeichen einer Quelle außerhalb des AI-Hub („Drittsystem · gelesen 20.10.2026“); nur lesend, mit Stand (Abweichung AW-141). */
  kennzeichen?: string;
}

export interface MatrixSpalte {
  id: string;
  kopf: string;
  /** Id der `MatrixSpaltenGruppe`; Spalten einer Gruppe stehen nebeneinander. */
  gruppe: string;
  /** Zweite Zeile im Kopf (z. B. „7 Personen“). */
  zusatz?: string;
  /** Der Kopf öffnet das Objekt (Rollen-Detail im Seitenpanel); ohne Ziel steht der Name ohne Link. */
  href?: string;
}

export interface MatrixZelle {
  erlaubt: boolean;
  /** Abweicht vom Standard des Manifests (Haken bzw. Strich mit Punkt). */
  abweichend?: boolean;
  /** Ausdrücklich ausgeschlossen (Symbol „gesperrt“), Vorrang vor `erlaubt`. */
  ausgeschlossen?: boolean;
}

export interface MatrixZeile {
  id: string;
  titel: string;
  zweitzeile?: string;
  /** Zusatz hinter dem Titel (z. B. „2 Personen ausgeschlossen“ mit Symbol), nur Zahlen (r177). */
  marke?: ReactNode;
  zellen: Readonly<Record<string, MatrixZelle>>;
}

export interface MatrixBand {
  id: string;
  titel: string;
  /** Zusatz hinter dem Titel („14 Rechte · 3 Themen“). */
  zusatz?: string;
  zeilen: MatrixZeile[];
}

/** Zustandswort je Zelle (Hilfstechnik und Legende); EINE Quelle. */
export function matrixZustandText(z: MatrixZelle): string {
  if (z.ausgeschlossen) return "ausgeschlossen";
  if (z.erlaubt) return z.abweichend ? "erlaubt, abweichend vom Standard" : "erlaubt";
  return z.abweichend ? "nicht erlaubt, abweichend vom Standard" : "nicht erlaubt";
}

const SYMBOL = "shrink-0 size-4";
const SYMBOL_PRIMAER = "shrink-0 size-4 text-primary";
const SYMBOL_GEDAEMPFT = "shrink-0 size-4 text-muted-foreground-1";
const SYMBOL_GEFAHR = "shrink-0 size-4 text-destructive";
const SYMBOL_PUNKT = "shrink-0 size-4 -ms-1.5 text-primary";

function ZellenSymbol({ zelle, beschriftung }: { zelle: MatrixZelle; beschriftung: string }) {
  const inhalt = zelle.ausgeschlossen ? (
    <Ban className={SYMBOL_GEFAHR} aria-hidden="true" />
  ) : zelle.erlaubt ? (
    <Check className={SYMBOL_PRIMAER} aria-hidden="true" />
  ) : (
    <Minus className={SYMBOL_GEDAEMPFT} aria-hidden="true" />
  );
  return (
    <span role="img" aria-label={beschriftung} className="inline-flex items-center justify-center">
      {inhalt}
      {zelle.abweichend && <Dot className={SYMBOL_PUNKT} aria-hidden="true" />}
    </span>
  );
}

const RAHMEN = "relative max-h-screen overflow-auto";
const GRUPPEN_KOPF =
  "sticky top-0 z-10 h-8 px-3 py-1.5 bg-card border-b border-s border-table-line text-center text-xs font-medium text-muted-foreground-1 uppercase whitespace-nowrap";
const ROLLEN_KOPF = "sticky top-8 z-10 px-3 py-2 bg-card border-b border-s border-table-line text-center text-sm font-medium text-foreground align-bottom";
const ECKE = "sticky start-0 top-0 z-30 bg-card border-b border-table-line px-4 py-3 text-start text-xs font-medium text-muted-foreground-1 uppercase min-w-64";
const BAND_ZELLE = "sticky start-0 z-10 bg-surface px-4 py-2 text-start text-sm text-surface-foreground";
const ZEILEN_KOPF = "sticky start-0 z-10 bg-card px-4 py-2.5 text-start font-normal align-middle min-w-64";
const WERT = "px-3 py-2.5 text-center align-middle border-s border-table-line";

export function MatrixTabelle({
  id,
  beschriftung,
  zeilenKopf,
  gruppen,
  spalten,
  baender,
  startEingeklappt = [],
  leer,
}: {
  id?: string;
  /** `<caption class="sr-only">`: nennt die Achsen („Rechte-Matrix: Rechte in den Zeilen, Rollen in den Spalten“). */
  beschriftung: string;
  /** Kopf der ersten Spalte („Recht“). */
  zeilenKopf: string;
  gruppen: MatrixSpaltenGruppe[];
  spalten: MatrixSpalte[];
  baender: MatrixBand[];
  /** Ids der Bänder, die zu Beginn eingeklappt sind. */
  startEingeklappt?: string[];
  /** Platz für den Satz „keine Treffer“; erscheint statt der Tabelle, wenn keine Zeile übrig ist. */
  leer?: ReactNode;
}) {
  const [zu, setZu] = useState<Set<string>>(() => new Set(startEingeklappt));
  const umschalten = (bandId: string) =>
    setZu((alt) => {
      const neu = new Set(alt);
      if (neu.has(bandId)) neu.delete(bandId);
      else neu.add(bandId);
      return neu;
    });
  if (baender.every((b) => b.zeilen.length === 0) && leer) return <>{leer}</>;

  // Gruppenkopf: eine Zelle je Gruppe, `colSpan` = Zahl der Spalten dieser Gruppe (Reihenfolge der Spalten).
  const kopfGruppen: { gruppe: MatrixSpaltenGruppe; anzahl: number }[] = [];
  for (const s of spalten) {
    const letzte = kopfGruppen[kopfGruppen.length - 1];
    if (letzte && letzte.gruppe.id === s.gruppe) letzte.anzahl += 1;
    else {
      const g = gruppen.find((x) => x.id === s.gruppe);
      if (g) kopfGruppen.push({ gruppe: g, anzahl: 1 });
    }
  }
  const gesamt = spalten.length + 1;

  return (
    <div className={RAHMEN}>
      {/* Preline: marketing/marketing-sub-pricing-conversion/marketing-cat-pricing-tables/plan-feature-comparison-table (Gruppenbänder, Zellen mit Symbol; Eigenbau mit echtem table, M-130, AW-140/AW-141) */}
      <table id={id} className="min-w-full border-separate border-spacing-0">
        <caption className="sr-only">{beschriftung}</caption>
        <thead>
          <tr>
            <th scope="col" rowSpan={2} className={ECKE}>
              {zeilenKopf}
            </th>
            {kopfGruppen.map(({ gruppe, anzahl }) => (
              <th key={gruppe.id} scope="colgroup" colSpan={anzahl} className={GRUPPEN_KOPF}>
                {gruppe.titel}
                {gruppe.kennzeichen && <span className="block normal-case font-normal">{gruppe.kennzeichen}</span>}
              </th>
            ))}
          </tr>
          <tr>
            {spalten.map((s) => (
              <th key={s.id} scope="col" className={ROLLEN_KOPF}>
                {s.href ? (
                  <Link href={s.href} scroll={false} className="text-foreground hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-md">
                    {s.kopf}
                  </Link>
                ) : (
                  s.kopf
                )}
                {s.zusatz && <span className="block text-xs font-normal text-muted-foreground-1">{s.zusatz}</span>}
              </th>
            ))}
          </tr>
        </thead>
        {baender.map((b) => {
          const eingeklappt = zu.has(b.id);
          const panelId = `${id ?? "matrix"}-${b.id}`;
          if (b.zeilen.length === 0) return null;
          return (
            <tbody key={b.id} id={panelId}>
              <tr>
                <th scope="rowgroup" className={BAND_ZELLE}>
                  <button
                    type="button"
                    aria-expanded={!eingeklappt}
                    aria-controls={panelId}
                    onClick={() => umschalten(b.id)}
                    className="inline-flex items-center gap-x-1.5 font-medium text-foreground focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-md"
                  >
                    {eingeklappt ? <ChevronRight className={SYMBOL} aria-hidden="true" /> : <ChevronDown className={SYMBOL} aria-hidden="true" />}
                    {b.titel}
                  </button>
                  {b.zusatz && <span className="ms-2 text-muted-foreground-1">{b.zusatz}</span>}
                </th>
                <td colSpan={gesamt - 1} className="bg-surface" />
              </tr>
              {!eingeklappt &&
                b.zeilen.map((z) => (
                  <Fragment key={z.id}>
                    <tr>
                      <th scope="row" className={ZEILEN_KOPF}>
                        <span className="block text-sm text-foreground">{z.titel}</span>
                        {z.zweitzeile && <span className="block text-xs text-muted-foreground-1">{z.zweitzeile}</span>}
                        {z.marke && <span className="mt-0.5 block text-xs text-muted-foreground-1">{z.marke}</span>}
                      </th>
                      {spalten.map((s) => {
                        const zelle = z.zellen[s.id];
                        return (
                          <td key={s.id} className={WERT}>
                            {zelle ? <ZellenSymbol zelle={zelle} beschriftung={`${z.titel}, ${s.kopf}: ${matrixZustandText(zelle)}`} /> : null}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                ))}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

/** Legende der Zellzustände (Symbol und Wort, nie nur Farbe); steht über oder neben der Matrix. */
export function MatrixLegende() {
  const eintrag = (zelle: MatrixZelle, text: string) => (
    <li key={text} className="inline-flex items-center gap-x-1.5">
      <ZellenSymbol zelle={zelle} beschriftung={text} />
      <span>{text}</span>
    </li>
  );
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground-1" aria-label="Legende">
      {eintrag({ erlaubt: true }, "erlaubt")}
      {eintrag({ erlaubt: false }, "nicht erlaubt")}
      {eintrag({ erlaubt: true, abweichend: true }, "abweichend vom Standard")}
      {eintrag({ erlaubt: false, ausgeschlossen: true }, "ausgeschlossen")}
    </ul>
  );
}
